import type { Resume } from "@adafia/types";
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type SensorDescriptor,
	type SensorOptions,
} from "@dnd-kit/core";
import {
	restrictToFirstScrollableAncestor,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { BookOpen } from "lucide-react";
import { InputGroup } from "./InputGroup";
import { SectionHeader } from "./SectionHeader";
import { SortableItem } from "./SortableItem";

interface EducationEditorProps {
	education: Resume["education"];
	onAdd: () => void;
	onUpdate: (id: string, field: string, value: string) => void;
	onRemove: (id: string) => void;
	onDragEnd: (event: DragEndEvent) => void;
	sensors: SensorDescriptor<SensorOptions>[];
}

export function EducationEditor({
	education,
	onAdd,
	onUpdate,
	onRemove,
	onDragEnd,
	sensors,
}: EducationEditorProps) {
	return (
		<>
			<SectionHeader title="Education" icon={BookOpen} onAdd={onAdd} />
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={onDragEnd}
				modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
			>
				<SortableContext
					items={education.map((e) => e.id)}
					strategy={verticalListSortingStrategy}
				>
					{education.map((edu) => (
						<SortableItem
							key={edu.id}
							id={edu.id}
							onDelete={() => onRemove(edu.id)}
						>
							<InputGroup
								label="School Name"
								value={edu.school}
								onChange={(v) => onUpdate(edu.id, "school", v)}
							/>
							<div className="form-grid">
								<InputGroup
									label="Degree"
									value={edu.degree}
									onChange={(v) => onUpdate(edu.id, "degree", v)}
								/>
								<InputGroup
									label="Year"
									value={edu.year}
									onChange={(v) => onUpdate(edu.id, "year", v)}
								/>
							</div>
						</SortableItem>
					))}
				</SortableContext>
			</DndContext>
		</>
	);
}
